/**
 * =========================================================================================
 * Component - UTable Master Column Visibility
 * =========================================================================================
 * Admin component for managing column visibility rules based on UAC (User Access Control).
 * Allows defining multiple configurations, each mapping a set of access levels to a set of columns.
 **/

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';
import SideModal from '../../../side-modal';
import UAsyncEmployeeSelect from '../../../UAsyncEmployeeSelect';
import UButton from "../../../UButton";
import AddNewButton from '../../UFilters/AddNewBtn/AddNewBtn';
import UPopup from "ubs-framework/v2/UPopup";
import { errorFocus, isMobileDevice, showToastSuccess } from '../../function';
import { COLOR } from '../../constant';
import UInput from "ubs-framework/v2/UInput";
import AddColumn from './add-column';
import { useForceUpdate } from '../../../hooks';
import Validator from 'simple-react-validator';
import { useUFramework } from '../../../context';
import Skeleton from '../../../skeleton';
import { ISkeleton } from '../../../skeleton/input-skeleton';

const MasterColumnVisibility = (props) => {
    const {
        columnList,
        tabKey,
    } = props;

    const { sendData, useSocket, ID, SOCKET_ID, S3_BASE_URL } = useUFramework(e => e);

    const forceUpdate = useForceUpdate();
    const validator = useRef(new Validator({
        element: (message, className) => <div className="u_error">{message}</div>,
        autoForceUpdate: {forceUpdate}
    }));

    const [masterColumnOpen, setMasterColumnOpen] = useState(false);
    const [masterConfigsDraft, setMasterConfigsDraft] = useState([]);
    const [loader, setLoader] = useState(false);
    const [loading, setLoading] = useState(true);
    
    const [addColumnOpen, setAddColumnOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [reorderedColumns, setReorderedColumns] = useState(null);

    // Form methods for AddColumn picklist
    const methods = useForm();
    const { handleSubmit, reset } = methods;

    const { off, refresh } = useSocket(res => {
        if (res && !res?.error && !res?.data?.error) {
            switch (res.event) {
                case 'columnControl|masterSetting|update':
                    setMasterColumnOpen(false);
                    setLoader(false);
                    showToastSuccess('Column visibility updated successfully!');
                    sendData('hrms|master|option|fetch', {user: ID, name: tabKey})
                    break;
                case 'columnControl|masterSetting|read':
                    const fetchedConfigs = res?.data?.configs;
                    const defaultMandatory = columnList?.filter(col => col?.isMandatory).map(col => col?.value) || [];
                    if (fetchedConfigs && Array.isArray(fetchedConfigs)) {                        
                        setMasterConfigsDraft(fetchedConfigs.map(config => ({
                            level1: config?.uacList?.map(e => ({ value: e?._id, label: e?.name })) || [],
                            columns: !!config?.values?.length ? config?.values : defaultMandatory
                        })));
                    } else {
                        setMasterConfigsDraft([{
                            level1: [],
                            columns: defaultMandatory
                        }]);
                    }
                    setLoading(false);
                    break;
                default:
                    return true;
            }
        }
    });

    useEffect(() => {
        refresh();
        return () => off();
    }, [tabKey, columnList]);

     useEffect(() => {
        if (masterColumnOpen) {
            const defaultMandatory = columnList?.filter(col => col?.isMandatory).map(col => col?.value) || [];
            setMasterConfigsDraft([{ level1: [], columns: defaultMandatory }]);
            setLoading(true);
            sendData('columnControl|masterSetting|read', { view: tabKey });
        }
    }, [masterColumnOpen, tabKey]);

    const handleAssignColumns = (config, index) => {
        setEditIndex(index);

        let configValues = {};
        if (config?.columns?.length > 0) {
            // Map saved column keys to checkbox "true" state
            config.columns.forEach(item => { configValues[item] = true; });
        } else {
            // Default: all columns visible
            columnList?.forEach(col => { configValues[col.value] = col.status; });
        }

        reset(configValues);
        setAddColumnOpen(true);
    };

    const handleColumnSave = (data) => {
        // Use reordered columns if available to preserve drag-and-drop order
        const sourceList = reorderedColumns && reorderedColumns.length > 0
            ? reorderedColumns
            : columnList;

        const selectedKeys = [];
        sourceList.forEach(col => {
            if (data[col.value] === true || col.isMandatory) {
                selectedKeys.push(col.value);
            }
        });

        const newConfigs = [...masterConfigsDraft];
        newConfigs[editIndex] = {
            ...newConfigs[editIndex],
            columns: selectedKeys
        };

        setMasterConfigsDraft(newConfigs);
        setAddColumnOpen(false);
        setEditIndex(null);
        setReorderedColumns(null);
    };

    const addConfiguration = () => {
        if (masterConfigsDraft.length > 0 && masterConfigsDraft[masterConfigsDraft.length - 1].level1.length === 0) {
            validator?.current?.showMessages();
            errorFocus();
            return;
        }
        const defaultMandatory = columnList?.filter(col => col.isMandatory).map(col => col.value) || [];
        setMasterConfigsDraft([...masterConfigsDraft, { level1: [], columns: defaultMandatory }]);
    };

    const removeConfiguration = (index) => {
        setMasterConfigsDraft(masterConfigsDraft.filter((_, i) => i !== index));
        validator.current.fields = {}
    };

    const handleMainSave = () => {
        if (validator?.current.allValid()) {
            if (sendData && tabKey) {
                const payload = {
                    view: tabKey,
                    configs: masterConfigsDraft.map(config => ({
                        uacList: config.level1?.map(item => item.value) || [],
                        values: config.columns || []
                    }))
                };
                
                setLoader(true);
                sendData('columnControl|masterSetting|update', payload);
            }
        } else {
            validator?.current?.showMessages();
            errorFocus()
        }
        
    };

    const getSelectedCount = (levels) => {
        return levels?.length > 0 ? `${levels.length}` : '';
    };

    const close = () => {
        setMasterColumnOpen(false)
        validator?.current?.hideMessages()
    }
    const getAssignColumnsCount = (index) => {
        const list = columnList.filter(col => col.visible !== false)
        const values = masterConfigsDraft[index]?.columns
        return list.filter(col => values?.includes(col.value)).length
    }
    
    return (
        <React.Fragment>
            {/* Gear Trigger Button */}
            <UPopup
                params={{ className: 'u_tooltip', disabled: isMobileDevice() }}
                desc={<span>Column Visibility Master Setting</span>}
            >
                <span className="u_filter-icon border u_mr-5" onClick={() => setMasterColumnOpen(true)}>
                    <Icon.Setting size={18} fill={COLOR.u_icon_bg} />
                </span>
            </UPopup>

            {/* Master SideModal */}
            <SideModal
                open={masterColumnOpen}
                className={'u_sidebar-modal'}
                maxWidth={600}
                closeOnOutsideClick={false}
                closeOnDocumentClick={false}
                onClose={() => close()}
                header={<div className='u_flex u_align-center'>
                    <h4 className='u_fw-700'>Column Visibility Master Setting</h4>
                    <UPopup inModal
                                params={{className: "u_tooltip u_tooltip-full"}}
                                desc={<span>It will show columns based on the UAC access level name assigned by the admin. If the Access Configuration is not selected, then it will show all columns.</span>}
                        >
                            <span className="u_rollover u_cp u_flex u_ml-10"><Icon.InfoTooltip fill={COLOR.u_icon_bg} size={16}/> </span>
                    </UPopup>
                    </div>}
                footer={
                    <div className="u_row u_pl-20 u_pr-20">
                        <div className='u_col-6'/>
                        <div className="u_col-3">
                            <UButton loading={loader} disabled={loader} variant="white" type="close" onClick={() => close()} />
                        </div>
                        <div className="u_col-3">
                            <UButton loading={loader} disabled={loader} variant="blue" type="save" cn="" onClick={handleMainSave} />
                        </div>
                    </div>
                }
            >
                <div className="u_p-20">
                    {loading ? (
                        Array.from(Array(2).keys()).map(key => (
                            <div key={key} className="u_radius-5 u_b-light u_mb-15 u_p-15">
                                <div className="u_flex u_align-center u_content-between u_mb-15">
                                    <div className="u_flex u_align-center">
                                        <Skeleton width="40px" height="24px" className="u_mr-10" />
                                        <Skeleton width="160px" height="18px" />
                                    </div>
                                    <Skeleton width="16px" height="16px" />
                                </div>
                                <div className="u_mb-15">
                                    <Skeleton width="90px" height="14px" className="u_mb-5" />
                                    <ISkeleton />
                                </div>
                                <div className="u_flex u_align-center u_content-between u_bt-light u_pt-15">
                                    <Skeleton width="140px" height="16px" />
                                    <Skeleton width="110px" height="16px" />
                                </div>
                            </div>
                        ))
                    ) : masterConfigsDraft?.map((config, index) => (
                        <div key={index} className="u_radius-5 u_b-light u_mb-15 u_p-15">
                            {/* Header: Badge + Title + Delete */}
                            <div className="u_flex u_align-center u_content-between u_mb-15">
                                <div className="u_flex u_align-center">
                                    <span className="u_bg-blue-1 u_color-blue u_radius-3 u_font u_fw-700 u_px-10 u_py-5 u_mr-10">
                                        {`#${String(index + 1).padStart(2, '0')}`}
                                    </span>
                                    <span className="u_font-16 u_fw-600 u_color-title">
                                        Access Configuration
                                    </span>
                                </div>
                                <span
                                    className="u_cp u_flex u_align-center"
                                    onClick={() => removeConfiguration(index)}
                                >
                                    <Icon.Delete fill={COLOR.u_red} size={16} />
                                </span>
                            </div>

                            {/* Access Level */}
                            <div className="u_mb-15">
                                <UInput
                                    label="Access Level"
                                    asterisk
                                    select
                                    selectValue={getSelectedCount(config?.level1)}
                                    hasError={validator?.current?.message(`assignUac-${index}`, config?.level1, 'required')}
                                >
                                    <UAsyncEmployeeSelect
                                        value={config?.level1}
                                        name={`assignUac-${index}`}
                                        onChange={(e) => {
                                            const newConfigs = [...masterConfigsDraft];
                                            newConfigs[index].level1 = e || [];
                                            setMasterConfigsDraft(newConfigs);
                                        }}
                                        params={{
                                            isMulti: true,
                                            menuPlacement: 'auto',
                                            menuPosition: 'fixed',
                                            inputId: `assignUac-${index}`,
                                            filterOption: (option) => {
                                                const usedIds = masterConfigsDraft
                                                    .filter((_, i) => i !== index)
                                                    .flatMap(c => (c?.level1 || []).map(u => u?.value));
                                                return !usedIds.includes(option?.value);
                                            }
                                        }}
                                        sendData={sendData}
                                        useSocket={useSocket}
                                        socketId={SOCKET_ID}
                                        s3Url={S3_BASE_URL}
                                        onlySearchUac={true}
                                        level={true}
                                    />
                                </UInput>
                            </div>

                            {/* Column Summary + Action */}
                            <div className="u_flex u_align-center u_content-between u_bt-light u_pt-15">
                                <span className="u_font u_color-title">
                                    {config?.columns?.length > 0 ? (
                                        <>
                                            <span className="u_color-blue u_fw-700">{config?.level1?.length > 0 ? getAssignColumnsCount(index) : 0}</span>
                                            {' Field Assigned'}
                                        </>
                                    ) : (
                                        'No columns assigned yet'
                                    )}
                                </span>
                                <div className="u_add-time-btn">
                                    <div
                                        type="button"
                                        className={`u_flex u_align-center u_cp u_p-0 ${config?.level1?.length > 0 ? '' : 'u_disabled'}`}
                                        onClick={() => handleAssignColumns(config, index)}
                                    >
                                        {config?.columns?.length > 0 ? <Icon.Edit fill={COLOR.u_blue} size={14} /> : <Icon.Plus fill={COLOR.u_blue} size={14} />}
                                        <span className="u_ml-10 u_font u_color-title ">
                                            {config?.columns?.length > 0 ? 'Edit Column' : 'Assign Column'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {!loading && (
                        <div className="u_flex u_mb-15">
                            <AddNewButton title="Configuration" action={addConfiguration} />
                        </div>
                    )}
                </div>
            </SideModal>

            {/* Nested Column Picker Modal */}
            {addColumnOpen && (
                <SideModal
                    open={addColumnOpen}
                    className={'u_sidebar-modal'}
                    maxWidth={340}
                    onClose={() => setAddColumnOpen(false)}
                    closeOnOutsideClick={false}
                    closeOnDocumentClick={false}
                    header={<h4 className='u_fw-700'>Assign Column</h4>}

                    position="right"
                    zIndex={10002} // Ensure it's above the master modal
                    footer={
                        <div className="u_row u_pl-20 u_pr-20">
                            <div className="u_col-6">
                                <UButton variant="white" type="cancel" onClick={() => setAddColumnOpen(false)} />
                            </div>
                            <div className="u_col-6">
                                <UButton variant="blue" type="done" cn="u_ml-10" onClick={handleSubmit(handleColumnSave)} />
                            </div>
                        </div>
                    }
                >
                    <FormProvider {...methods}>
                        <AddColumn
                            column={columnList.filter(col => col.visible !== false)}
                            tabKey={tabKey}
                            savedOrder={masterConfigsDraft[editIndex]?.columns}
                            onColumnReorder={setReorderedColumns}
                            isDraggable={true}
                        />
                    </FormProvider>
                </SideModal>
            )}
        </React.Fragment>
    );
};

MasterColumnVisibility.propTypes = {
    columnList: PropTypes.array.isRequired,
    tabKey: PropTypes.string,
    savedOrder: PropTypes.array,
};

MasterColumnVisibility.defaultProps = {
    savedOrder: [],
};

export default MasterColumnVisibility;
